import { Injectable, OnDestroy } from '@angular/core';

/**
 * Material Symbols icons are ligature *text* — `<mat-icon>chevron_right</mat-icon>` —
 * so CSS has no way to select "the back arrow" and mirror it in Arabic. There are
 * ~1100 icon usages across the portal and a large share bind the name dynamically
 * (`<mat-icon>{{ expanded ? 'chevron_left' : 'chevron_right' }}</mat-icon>`), so
 * hand-tagging each directional one is not maintainable.
 *
 * This service does the one thing CSS can't: read each icon's name and stamp
 * `.rtl-mirror` on the directional ones. The actual flip lives in styles.scss and
 * only applies under `[dir="rtl"]`, so tagging is direction-agnostic — it runs
 * once and needs no re-run when the language changes.
 *
 * Escape hatches: add `rtl-mirror` by hand to a custom SVG/img that needs the
 * same treatment, or `rtl-no-mirror` to keep a listed icon upright.
 */
@Injectable({ providedIn: 'root' })
export class RtlIconService implements OnDestroy {
  /**
   * Icons whose meaning is tied to reading direction. Deliberately an allowlist,
   * not a heuristic: flipping the wrong glyph (a credit card, a trend line, a
   * clock) is far more visible than missing one.
   */
  private static readonly DIRECTIONAL = new Set([
    // Chevrons / carets
    'chevron_left', 'chevron_right',
    'keyboard_arrow_left', 'keyboard_arrow_right',
    'keyboard_double_arrow_left', 'keyboard_double_arrow_right',
    'navigate_before', 'navigate_next',
    // Arrows
    'arrow_back', 'arrow_forward',
    'arrow_back_ios', 'arrow_forward_ios', 'arrow_back_ios_new',
    'arrow_left', 'arrow_right',
    'arrow_left_alt', 'arrow_right_alt',
    'arrow_circle_left', 'arrow_circle_right',
    'east', 'west',
    'trending_flat', 'double_arrow',
    'subdirectory_arrow_left', 'subdirectory_arrow_right',
    // Pagination
    'first_page', 'last_page',
    // Actions with a direction of travel
    'logout', 'login', 'exit_to_app',
    'reply', 'reply_all', 'forward', 'send',
    'undo', 'redo',
    'swap_horiz',
    'call_made', 'call_received',
    'format_indent_increase', 'format_indent_decrease',
  ]);

  private static readonly ICON_SELECTOR = 'mat-icon, .material-icons, .material-symbols-outlined';

  // Explicit `| undefined` rather than `?:` — the project runs
  // exactOptionalPropertyTypes, which forbids assigning undefined to `?:`.
  private observer: MutationObserver | undefined;

  /** Idempotent — safe to call more than once. */
  init(): void {
    if (this.observer || typeof document === 'undefined') return;

    this.tagWithin(document.body);

    this.observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type === 'characterData') {
          // A dynamically bound icon name changed in place; re-evaluate its host.
          const host = record.target.parentElement;
          if (host) this.tagElement(host);
          continue;
        }
        record.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) this.tagWithin(node as Element);
        });
      }
    });

    // NOTE: attributes are intentionally NOT observed. Tagging sets a class,
    // which would re-trigger the observer and loop.
    this.observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  private tagWithin(root: Element): void {
    if (root.matches?.(RtlIconService.ICON_SELECTOR)) this.tagElement(root);
    root.querySelectorAll?.(RtlIconService.ICON_SELECTOR).forEach((el) => this.tagElement(el));
  }

  private tagElement(el: Element): void {
    if (!el.matches?.(RtlIconService.ICON_SELECTOR)) return;
    if (el.classList.contains('rtl-no-mirror')) return;

    const name = el.textContent?.trim();
    const shouldMirror = !!name && RtlIconService.DIRECTIONAL.has(name);

    // Guard both branches: setting a class that's already correct still counts as
    // an attribute write, and this runs on every DOM insertion.
    if (shouldMirror && !el.classList.contains('rtl-mirror')) {
      el.classList.add('rtl-mirror');
    } else if (!shouldMirror && el.classList.contains('rtl-mirror')) {
      el.classList.remove('rtl-mirror');
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.observer = undefined;
  }
}
